import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Button, Stack } from '@mui/material';
import HandoverConfirmDialog from './HandoverConfirmDialog';
import ConfirmationDialog from './ConfirmationDialog';
import pettyCashApi from '../../services/pettyCashApi';
import { showSnackbar } from '../../store/slices/uiSlice';
import type { HandoverConfirmInput, HandoverView, PettyCashConfig } from '../../types/pettyCash';
import { formatDate, processSettings, quarterLabel } from '../../utils/pettyCash';

/**
 * What the signed-in user must do with his petty cash, above the claims: a handover to confirm (as the outgoing
 * holder or the successor), and the quarterly confirmation while it is due.
 */

interface Props {
  config: PettyCashConfig;
  companyId: number;
  /** After a confirmation: reload the configuration (holder, banners) and the list. */
  onChanged: () => void;
}

const HolderBanners: React.FC<Props> = ({ config, companyId, onChanged }) => {
  const dispatch = useDispatch();
  // The last handover stays while its dialog closes.
  const [handover, setHandover] = useState<{ open: boolean; view: HandoverView | null; side: 'from' | 'to' }>({ open: false, view: null, side: 'from' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const currency = config.company.currency;
  const due = config.confirmationDue;
  const pending = config.pendingHandovers || [];

  const confirmHandover = async (handoverId: number, input: HandoverConfirmInput) => {
    const view = await pettyCashApi.confirmHandover(handoverId, input);
    setHandover((h) => ({ ...h, open: false }));
    dispatch(showSnackbar({
      message: view.status === 'Completed'
        ? `You now hold the petty cash of ${view.from.name}`
        : `Handover confirmed: ${view.to.name} confirms receiving it next`,
      severity: 'success',
    }));
    onChanged();
  };

  const shown = pending.length > 0 || (!!due && !due.confirmed);
  return (
    <>
      {shown ? (
        <Stack spacing={1}>
          {pending.map((h) => {
            const side: 'from' | 'to' | null = h.canConfirmFrom ? 'from' : h.canConfirmTo ? 'to' : null;
            const text = side === 'to'
              ? `You have a petty cash handover to confirm: ${h.from.name} hands the petty cash over to you.`
              : `You have a petty cash handover to confirm: count your cash and hand it over to ${h.to.name}.`;
            return (
              <Alert
                key={h.handoverId}
                severity="warning"
                action={side ? (
                  <Button color="inherit" size="small" onClick={() => setHandover({ open: true, view: h, side })}>Confirm</Button>
                ) : undefined}
              >
                {side ? text : `Petty cash handover from ${h.from.name} to ${h.to.name}: waiting for ${h.fromConfirmedAt ? h.to.name : h.from.name} to confirm.`}
              </Alert>
            );
          })}
          {due && !due.confirmed ? (
            <Alert
              severity="info"
              action={<Button color="inherit" size="small" onClick={() => setConfirmOpen(true)}>Confirm now</Button>}
            >
              Your quarterly petty cash confirmation for {quarterLabel(due.quarter)} is due by {formatDate(due.windowEnd)}.
            </Alert>
          ) : null}
        </Stack>
      ) : null}
      {/* Mounted even when no banner is left, so a dialog can finish closing. */}
      <HandoverConfirmDialog
        open={handover.open}
        handover={handover.view}
        side={handover.side}
        currency={currency}
        onClose={() => setHandover((h) => ({ ...h, open: false }))}
        onConfirm={confirmHandover}
      />
      <ConfirmationDialog
        open={confirmOpen}
        companyId={companyId}
        currency={currency}
        tolerance={processSettings(config.settings).imprestTolerance}
        onClose={() => setConfirmOpen(false)}
        onDone={(view) => {
          setConfirmOpen(false);
          dispatch(showSnackbar({ message: `Confirmation for ${quarterLabel(view.quarter)} saved`, severity: 'success' }));
          onChanged();
        }}
      />
    </>
  );
};

export default HolderBanners;
